import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getSession, sessionExpiresSoon, setSession } from "@/lib/session.js";
import { refreshSession } from "@/lib/license-api.js";

/**
 * Wrap protected routes. Redirects to /login if the user has no session.
 * Best-effort refresh if the session expires within 3 days.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState(() => getSession());
  const location = useLocation();

  useEffect(() => {
    if (!session) return;
    if (!sessionExpiresSoon()) return;
    void (async () => {
      try {
        const fresh = await refreshSession(session.jwt);
        const updated = setSession(fresh.jwt);
        setSessionState(updated);
      } catch {
        // Silent — user will be re-prompted on next protected call
      }
    })();
  }, [session]);

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}
